import Medusa from "@medusajs/js-sdk"

// export const backendUrl = __BACKEND_URL__ ?? "https://admin.beautobe.com"
export const backendUrl = import.meta.env.VITE_MEDUSA_BACKEND_URL
export const publishableApiKey = __PUBLISHABLE_API_KEY__ ?? ""

export const sdk = new Medusa({
  baseUrl: backendUrl,
  publishableKey: publishableApiKey,
})

export type ProductImportValidationIssue = {
  severity: "error" | "warning"
  code: string
  message: string
  row: number
  column?: string
  raw_value?: string | number | boolean | null
  normalized_value?: string | number | boolean | null
  product_handle?: string
}

export type ProductImportValidationSummary = {
  total_rows: number
  product_count: number
  error_count: number
  warning_count: number
}

export type ProductImportValidationResult = {
  can_import: boolean
  issues: ProductImportValidationIssue[]
  summary: ProductImportValidationSummary
}

export type ProductImportValidationEnvelope = {
  validation: ProductImportValidationResult
}

export type ApiError = Error & {
  details?: any
}

// useful when you want to call the BE from the console and try things out quickly
if (typeof window !== "undefined") {
  ; (window as any).__sdk = sdk
}

export const importProductsQuery = async (file: File) => {
  const formData = new FormData()
  formData.append("file", file)

  return fetchQuery("/vendor/products/import", {
    method: "POST",
    body: formData,
  })
}

export const validateProductImportQuery = async (
  file: File
): Promise<ProductImportValidationEnvelope> => {
  const formData = new FormData()
  formData.append("file", file)

  return fetchQuery("/vendor/products/import/validate", {
    method: "POST",
    body: formData,
  })
}

export const uploadFilesQuery = async (files: any[]) => {
  const formData = new FormData()

  for (const { file } of files) {
    formData.append("files", file)
  }

  return fetchQuery("/vendor/uploads", {
    method: "POST",
    body: formData,
  })
}

export const fetchQuery = async (
  url: string,
  {
    method,
    body,
    query,
    headers,
  }: {
    method: "GET" | "POST" | "DELETE" | "PATCH"
    body?: object | FormData
    query?: Record<
      string,
      string | number | boolean | Array<string | number | boolean>
    >
    headers?: { [key: string]: string }
  }
) => {
  const bearer = (await window.localStorage.getItem("medusa_auth_token")) || ""
  const searchParams = new URLSearchParams()
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") {
      return
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== "") {
          searchParams.append(key, String(item))
        }
      })

      return
    }

    searchParams.append(key, String(value))
  })
  const isFormData = typeof FormData !== "undefined" && body instanceof FormData
  const defaultHeaders: { [key: string]: string } = {
    authorization: `Bearer ${bearer}`,
    "x-publishable-api-key": publishableApiKey,
  }
  // Only set Content-Type for non-FormData requests; browser will set boundary for FormData
  if (!isFormData) {
    defaultHeaders["Content-Type"] = "application/json"
  }

  const qs = searchParams.toString()
  const response = await fetch(`${backendUrl}${url}${qs ? `?${qs}` : ""}`, {
    method: method,
    headers: {
      ...defaultHeaders,
      ...headers,
    },
    body: body ? (isFormData ? (body as FormData) : JSON.stringify(body)) : null,
  })

  if (!response.ok) {
    let message = "Unknown server error"
    let errorData: any
    try {
      const contentType = response.headers.get("content-type") || ""
      if (contentType.includes("application/json")) {
        errorData = await response.json()
        message = errorData?.message || message
      } else {
        const text = await response.text()
        message = text || response.statusText || message
      }
    } catch (_) {
      // ignore parse errors, use default message below
    }

    const error = new Error(message) as ApiError
    error.details = errorData

    throw error
  }

  return response.json()
}
